import { useMemo } from 'react'

import { useTheme } from '@chakra-ui/react'
import moment from 'moment'
import { isNil } from 'lodash-es'

import { useTranscriptClient } from 'src/contexts/Transcript/TranscriptContext'
import { useConfig } from 'src/contexts/ConfigurationContext'

import * as S from './styles'
import { PARAGRAPH_TIMESTAMP_UNKNOWN_PRESENTATION, TIME_FORMAT_DESCRIPTORS } from './constants'

type ParagraphTimestampProps = { timeRelative: number | null; timeAbsolute: number | null }

export const ParagraphTimestamp = (props: ParagraphTimestampProps) => {
    const { timeAbsolute, timeRelative } = props
    const transcriptClient = useTranscriptClient()
    const recordingStartedAt = transcriptClient.getRecordingStartedAt()

    const [{ timeFormat }] = useConfig()
    const { momentFormat, containerWidthEm } = TIME_FORMAT_DESCRIPTORS[timeFormat]

    const formattedParagraphTime = useMemo(() => {
        if (isNil(timeAbsolute) && isNil(timeRelative)) {
            return PARAGRAPH_TIMESTAMP_UNKNOWN_PRESENTATION
        }

        if (isNil(timeAbsolute)) {
            return formatParagraphTime({
                type: 'relative',
                sessionStartTime: recordingStartedAt,
                relativeParagraphTimeInSeconds: timeRelative || 0,
                format: momentFormat,
            })
        }

        return formatParagraphTime({ type: 'absolute', absoluteParagraphTimeInSeconds: timeAbsolute, format: momentFormat })
    }, [momentFormat, recordingStartedAt, timeAbsolute, timeRelative])

    const {
        textColorIndex,
        palette: {
            transcript: { timestampColors },
        },
    } = useTheme()
    const textColor = timestampColors[textColorIndex]

    return (
        <S.Timestamp $color={textColor} $minWidth={containerWidthEm}>
            {formattedParagraphTime}
        </S.Timestamp>
    )
}

type FormatParagraphTimeParams =
    | { type: 'absolute'; format: string; absoluteParagraphTimeInSeconds: number }
    | { type: 'relative'; format: string; sessionStartTime: number; relativeParagraphTimeInSeconds: number }

const formatParagraphTime = (params: FormatParagraphTimeParams) =>
    params.type === 'absolute'
        ? moment(params.absoluteParagraphTimeInSeconds * 1000).format(params.format)
        : moment(params.sessionStartTime * 1000)
              .add(params.relativeParagraphTimeInSeconds, 'seconds')
              .format(params.format)
